export const ORDINAL_MARKET_INTENT_VERSION = 1 as const
export const DEFAULT_ORDINAL_MARKET_INTENT_TTL_MS = 30 * 60 * 1000

export type OrdinalMarketOperation =
    | 'ordinal_market_listing_parent'
    | 'ordinal_market_listing_purchase'
    | 'ordinal_market_offer'

export type OrdinalMarketApprovalRole =
    | 'seller_listing'
    | 'buyer_purchase'
    | 'buyer_offer'
    | 'seller_accept'

export type OrdinalMarketPublicIdentity = {
    userId: string
    paymentAddress: string
    ordinalsAddress: string
    walletGeneration: string
}

export type OrdinalMarketTreechatFee = {
    address: string
    satoshis: number
}

export type OrdinalMarketExpectedChange = {
    role: 'buyer_change' | 'seller_change'
    address: string
    satoshis: number
}

/**
 * Public action terms retained across a WebView remount. This deliberately
 * excludes keys, signatures, unlocking scripts, and raw transactions.
 */
export type OrdinalMarketIntent = {
    version: typeof ORDINAL_MARKET_INTENT_VERSION
    operation: OrdinalMarketOperation
    authorizationId: string
    orderId: string
    signingRequestId: string
    attemptId: string
    manifestDigest: string
    approvalRole: OrdinalMarketApprovalRole
    assignedInputIndexes: number[]
    userId: string
    deviceId: string
    walletGeneration: string
    sourceOutpoint: string
    parentTxid?: string
    priceSats: number
    sellerProceedsSats: number
    treechatFee: OrdinalMarketTreechatFee
    layoutVersion: 2
    seller: OrdinalMarketPublicIdentity
    buyer?: OrdinalMarketPublicIdentity
    expectedChange: OrdinalMarketExpectedChange | null
    maxNetworkFeeSatoshis: number
    createdAt: number
    expiresAt: number
}

export type OrdinalMarketIntentDraft = Omit<OrdinalMarketIntent, 'createdAt' | 'expiresAt'>

export type OrdinalMarketIntentStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const HEX_32_BYTES = /^[0-9a-f]{64}$/i
const OUTPOINT = /^[0-9a-f]{64}_[0-9]+$/i
const STORAGE_PREFIX = 'treechat:ordinal-market-intent:v1:'

const requireText = (name: string, value: string): void => {
    if (!value) throw new Error(`${name} is required`)
}

const requireInteger = (name: string, value: number, minimum = 0): void => {
    if (!Number.isSafeInteger(value) || value < minimum) {
        throw new Error(`${name} must be a safe integer >= ${minimum}`)
    }
}

const validateIdentity = (name: string, identity: OrdinalMarketPublicIdentity): void => {
    requireText(`${name}.userId`, identity.userId)
    requireText(`${name}.paymentAddress`, identity.paymentAddress)
    requireText(`${name}.ordinalsAddress`, identity.ordinalsAddress)
    if (!HEX_32_BYTES.test(identity.walletGeneration)) {
        throw new Error(`${name}.walletGeneration must be a SHA-256 digest`)
    }
}

const copyIdentity = (identity: OrdinalMarketPublicIdentity): OrdinalMarketPublicIdentity => ({
    userId: identity.userId,
    paymentAddress: identity.paymentAddress,
    ordinalsAddress: identity.ordinalsAddress,
    walletGeneration: identity.walletGeneration,
})

function validateIntent(intent: OrdinalMarketIntent): void {
    if (intent.version !== ORDINAL_MARKET_INTENT_VERSION) {
        throw new Error('unsupported ordinal market intent version')
    }
    requireText('authorizationId', intent.authorizationId)
    requireText('orderId', intent.orderId)
    requireText('signingRequestId', intent.signingRequestId)
    requireText('attemptId', intent.attemptId)
    requireText('userId', intent.userId)
    requireText('deviceId', intent.deviceId)
    const validOperation =
        intent.operation === 'ordinal_market_listing_parent' ||
        intent.operation === 'ordinal_market_listing_purchase' ||
        intent.operation === 'ordinal_market_offer'
    const validApproval =
        (intent.operation === 'ordinal_market_listing_parent' &&
            intent.approvalRole === 'seller_listing') ||
        (intent.operation === 'ordinal_market_listing_purchase' &&
            intent.approvalRole === 'buyer_purchase') ||
        (intent.operation === 'ordinal_market_offer' &&
            (intent.approvalRole === 'buyer_offer' || intent.approvalRole === 'seller_accept'))
    if (!validOperation || !validApproval) {
        throw new Error('ordinal market operation and approval role do not match')
    }
    if (!HEX_32_BYTES.test(intent.manifestDigest)) {
        throw new Error('manifestDigest must be a SHA-256 digest')
    }
    if (!HEX_32_BYTES.test(intent.walletGeneration)) {
        throw new Error('walletGeneration must be a SHA-256 digest')
    }
    if (!OUTPOINT.test(intent.sourceOutpoint)) {
        throw new Error('sourceOutpoint must use txid_vout format')
    }
    if (intent.parentTxid !== undefined && !HEX_32_BYTES.test(intent.parentTxid)) {
        throw new Error('parentTxid must be a 32-byte transaction id')
    }
    if (
        intent.operation === 'ordinal_market_listing_purchase'
            ? !intent.parentTxid
            : intent.parentTxid
    ) {
        throw new Error('parentTxid is required only for listing purchases')
    }
    requireInteger('priceSats', intent.priceSats, 1)
    requireInteger('sellerProceedsSats', intent.sellerProceedsSats, 1)
    requireText('treechatFee.address', intent.treechatFee.address)
    requireInteger('treechatFee.satoshis', intent.treechatFee.satoshis, 1)
    if (intent.sellerProceedsSats + intent.treechatFee.satoshis !== intent.priceSats) {
        throw new Error('seller proceeds and Treechat fee must equal the price')
    }
    if (intent.layoutVersion !== 2) throw new Error('ordinal market payout layout must be v2')
    validateIdentity('seller', intent.seller)
    if (intent.buyer) validateIdentity('buyer', intent.buyer)
    if (intent.operation === 'ordinal_market_listing_parent' ? intent.buyer : !intent.buyer) {
        throw new Error('buyer identity is required after the listing parent')
    }
    const actor =
        intent.approvalRole === 'seller_listing' || intent.approvalRole === 'seller_accept'
            ? intent.seller
            : intent.buyer!
    if (
        intent.userId !== actor.userId ||
        intent.walletGeneration.toLowerCase() !== actor.walletGeneration.toLowerCase()
    ) {
        throw new Error('ordinal market intent actor does not match its wallet identity')
    }
    if (intent.expectedChange) {
        requireText('expectedChange.address', intent.expectedChange.address)
        requireInteger('expectedChange.satoshis', intent.expectedChange.satoshis, 1)
        const expectedRole =
            intent.operation === 'ordinal_market_listing_parent' ? 'seller_change' : 'buyer_change'
        const expectedAddress =
            expectedRole === 'seller_change'
                ? intent.seller.paymentAddress
                : intent.buyer!.paymentAddress
        if (
            intent.expectedChange.role !== expectedRole ||
            intent.expectedChange.address !== expectedAddress
        ) {
            throw new Error('ordinal market intent change does not match the transaction role')
        }
    }
    requireInteger('maxNetworkFeeSatoshis', intent.maxNetworkFeeSatoshis)
    requireInteger('createdAt', intent.createdAt)
    requireInteger('expiresAt', intent.expiresAt)
    if (intent.expiresAt <= intent.createdAt)
        throw new Error('ordinal market intent TTL is invalid')
    if (
        intent.assignedInputIndexes.length < 1 ||
        new Set(intent.assignedInputIndexes).size !== intent.assignedInputIndexes.length
    ) {
        throw new Error('assignedInputIndexes must be a non-empty unique list')
    }
    intent.assignedInputIndexes.forEach((index, offset) => {
        requireInteger(`assignedInputIndexes[${offset}]`, index)
        if (offset > 0 && index <= intent.assignedInputIndexes[offset - 1]) {
            throw new Error('assignedInputIndexes must be strictly ordered')
        }
    })
}

function defaultStorage(): OrdinalMarketIntentStorage {
    if (!globalThis.localStorage) throw new Error('localStorage is unavailable')
    return globalThis.localStorage
}

export function ordinalMarketIntentKey(
    authorizationId: string,
    approvalRole: OrdinalMarketApprovalRole,
): string {
    requireText('authorizationId', authorizationId)
    return `${STORAGE_PREFIX}${encodeURIComponent(authorizationId)}:${approvalRole}`
}

/** Saves only the explicitly selected public intent fields. */
export function saveOrdinalMarketIntent(
    draft: OrdinalMarketIntentDraft,
    options: {
        storage?: OrdinalMarketIntentStorage
        now?: number
        ttlMs?: number
    } = {},
): OrdinalMarketIntent {
    const now = options.now ?? Date.now()
    const ttlMs = options.ttlMs ?? DEFAULT_ORDINAL_MARKET_INTENT_TTL_MS
    requireInteger('intent timestamp', now)
    requireInteger('intent TTL', ttlMs, 1)
    const intent: OrdinalMarketIntent = {
        version: draft.version,
        operation: draft.operation,
        authorizationId: draft.authorizationId,
        orderId: draft.orderId,
        signingRequestId: draft.signingRequestId,
        attemptId: draft.attemptId,
        manifestDigest: draft.manifestDigest.toLowerCase(),
        approvalRole: draft.approvalRole,
        assignedInputIndexes: [...draft.assignedInputIndexes],
        userId: draft.userId,
        deviceId: draft.deviceId,
        walletGeneration: draft.walletGeneration.toLowerCase(),
        sourceOutpoint: draft.sourceOutpoint.toLowerCase(),
        ...(draft.parentTxid ? { parentTxid: draft.parentTxid.toLowerCase() } : {}),
        priceSats: draft.priceSats,
        sellerProceedsSats: draft.sellerProceedsSats,
        treechatFee: {
            address: draft.treechatFee.address,
            satoshis: draft.treechatFee.satoshis,
        },
        layoutVersion: draft.layoutVersion,
        seller: copyIdentity(draft.seller),
        ...(draft.buyer ? { buyer: copyIdentity(draft.buyer) } : {}),
        expectedChange: draft.expectedChange
            ? {
                  role: draft.expectedChange.role,
                  address: draft.expectedChange.address,
                  satoshis: draft.expectedChange.satoshis,
              }
            : null,
        maxNetworkFeeSatoshis: draft.maxNetworkFeeSatoshis,
        createdAt: now,
        expiresAt: now + ttlMs,
    }
    validateIntent(intent)
    const storage = options.storage ?? defaultStorage()
    storage.setItem(
        ordinalMarketIntentKey(intent.authorizationId, intent.approvalRole),
        JSON.stringify(intent),
    )
    return intent
}

export function loadOrdinalMarketIntent(
    authorizationId: string,
    approvalRole: OrdinalMarketApprovalRole,
    options: { storage?: OrdinalMarketIntentStorage; now?: number } = {},
): OrdinalMarketIntent | null {
    const storage = options.storage ?? defaultStorage()
    const key = ordinalMarketIntentKey(authorizationId, approvalRole)
    const serialized = storage.getItem(key)
    if (!serialized) return null
    try {
        const intent = JSON.parse(serialized) as OrdinalMarketIntent
        validateIntent(intent)
        if (intent.authorizationId !== authorizationId || intent.approvalRole !== approvalRole) {
            throw new Error('ordinal market intent storage key mismatch')
        }
        if (intent.expiresAt <= (options.now ?? Date.now())) {
            storage.removeItem(key)
            return null
        }
        return intent
    } catch {
        storage.removeItem(key)
        return null
    }
}

/**
 * Loads retry state only for the exact server signing attempt. A new Buy or
 * Accept attempt may reuse the authorization and role while carrying a fresh
 * request id, approval digest, and funded transaction.
 */
export function loadOrdinalMarketIntentForRequest(
    authorizationId: string,
    approvalRole: OrdinalMarketApprovalRole,
    signingRequestId: string,
    attemptId: string,
    options: { storage?: OrdinalMarketIntentStorage; now?: number } = {},
): OrdinalMarketIntent | null {
    const intent = loadOrdinalMarketIntent(authorizationId, approvalRole, options)
    if (!intent) return null
    if (intent.signingRequestId === signingRequestId && intent.attemptId === attemptId) {
        return intent
    }

    clearOrdinalMarketIntent(authorizationId, approvalRole, options.storage ?? defaultStorage())
    return null
}

export function clearOrdinalMarketIntent(
    authorizationId: string,
    approvalRole: OrdinalMarketApprovalRole,
    storage: OrdinalMarketIntentStorage = defaultStorage(),
): void {
    storage.removeItem(ordinalMarketIntentKey(authorizationId, approvalRole))
}
